import * as engine from '@/ai/engine'
import { RENJU, CONFIGS } from './settings'

const state = {
  loadingProgress: 0.0,
  ready: false,
  startSize: 0,
  restart: false,
  thinking: false,
  timeUsed: 0,
  lastThinkTime: 0,
  lastThinkPosition: [],
  currentConfig: null,
  hashSize: null,
  outputs: {
    pos: null,
    swap: null,
    currentPV: 0,
    pv: [
      {
        depth: 0,
        seldepth: 0,
        nodes: 0,
        eval: '-',
        winrate: 0.0,
        bestline: [],
      },
    ],
    nodes: 0,
    speed: 0,
    time: 0,
    msg: null,
    realtime: {
      best: [],
      lost: [],
    },
    forbid: [],
    attackFour: [],
    vcf: [],
    livingThree: [],
    threatAnalysis: {
      black: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 },
      white: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 }
    },
    error: null,
  },
  messages: [],
  posCallback: null,
}

const getters = {
  bestlineStr: (state) => (pvIdx) => {
    if (!pvIdx) pvIdx = 0
    let posStrs = []
    for (let p of state.outputs.pv[pvIdx].bestline) {
      const coordX = String.fromCharCode('A'.charCodeAt(0) + p[0])
      const coordY = (state.startSize - p[1]).toString()
      posStrs.push(coordX + coordY)
    }
    return posStrs.join(' ')
  },
}

const mutations = {
  setLoadingProgress(state, progress) {
    state.loadingProgress = progress
  },
  setReady(state, ready) {
    state.ready = ready
  },
  setThinkingState(state, thinking) {
    state.thinking = thinking
  },
  setStartSize(state, size) {
    state.startSize = size
  },
  setRestart(state, enabled = true) {
    state.restart = enabled
  },
  clearUsedTime(state) {
    state.timeUsed = 0
  },
  addUsedTime(state) {
    state.timeUsed += Date.now() - state.lastThinkTime
  },
  setThinkStartTime(state) {
    state.lastThinkTime = Date.now()
  },
  setCurrentConfig(state, config) {
    state.currentConfig = config
  },
  setHashSize(state, hashSize) {
    state.hashSize = hashSize
  },
  addMessage(state, msg) {
    state.messages.push(msg)
  },
  clearMessage(state) {
    state.messages = []
  },
  setOutput(state, output) {
    state.outputs[output.key] = output.value
  },
  setCurrentPV(state, pvIdx) {
    state.outputs.currentPV = pvIdx
  },
  setPVOutput(state, output) {
    let pv = state.outputs.pv[state.outputs.currentPV]
    if (!pv) {
      state.outputs.pv[state.outputs.currentPV] = {}
      pv = state.outputs.pv[state.outputs.currentPV]
    }
    pv[output.key] = output.value
  },
  clearOutput(state) {
    state.outputs.pv = [
      {
        depth: 0,
        seldepth: 0,
        nodes: 0,
        eval: '-',
        winrate: 0.0,
        bestline: [],
      },
    ]
    state.outputs.pos = null
    state.outputs.nodes = 0
    state.outputs.speed = 0
    state.outputs.forbid = []
    state.outputs.attackFour = []
    state.outputs.vcf = []
    state.outputs.livingThree = []
    // 不清空威胁分析数据，保持上一次的分析结果
    // state.outputs.threatAnalysis = {
    //   black: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 },
    //   white: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 }
    // }
  },
  addRealtime(state, rt) {
    if (rt.type === 'best') {
      // 对于最佳点，保存位置和评估信息
      state.outputs.realtime[rt.type].push({
        pos: rt.pos,
        eval: rt.eval || null
      })
    } else if (rt.type === 'lost') {
      // 对于必败点，保存位置和评估信息
      state.outputs.realtime[rt.type].push({
        pos: rt.pos,
        eval: rt.eval || null
      })
    }
  },
  updateRealtimeEval(state, { type, index, evalValue }) {
    // 更新指定类型和索引的实时数据评估值
    if (state.outputs.realtime[type] && state.outputs.realtime[type][index]) {
      state.outputs.realtime[type][index].eval = evalValue
    }
  },
  clearRealtime(state, type) {
    // 清空指定类型的实时数据
    state.outputs.realtime[type] = []
  },
  clearAllRealtime(state) {
    // 清空所有实时数据
    state.outputs.realtime.best = []
    state.outputs.realtime.lost = []
  },
  setRealtimeBest(state, points) {
    // 完全替换realtime.best数据
    state.outputs.realtime.best = points
  },
  setPosCallback(state, callback) {
    state.posCallback = callback
  },
  sortPV(state) {
    let isPosEqual = (m) =>
      state.outputs.pos ? m[0] == state.outputs.pos[0] && m[1] == state.outputs.pos[1] : false
    let evalStrToEval = (e) => {
      let val = +e
      if (isNaN(val)) {
        if (e.startsWith('+M')) val = 40000 - +e.substring(2)
        else if (e.startsWith('-M')) val = -40000 + +e.substring(2)
        else val = -80000
      }
      return val
    }

    state.outputs.pv.sort((a, b) => {
      if (isPosEqual(a.bestline[0])) return -1
      else if (isPosEqual(b.bestline[0])) return 1
      return evalStrToEval(b.eval) - evalStrToEval(a.eval)
    })
  },
  setLastThinkPosition(state, position) {
    state.lastThinkPosition = [...position]
  },
}

const actions = {
  async initEngine({ commit, dispatch, state, rootState }) {
    commit('setLoadingProgress', 0.0)
    commit('setReady', false)
    const callback = (r) => {
      if (r.realtime) {
                switch (r.realtime.type) {
          case 'LOST': {
            // 获取当前必败点的评估值
            let lostEval = state.outputs.pv[0] ? state.outputs.pv[0].eval : null
            commit('addRealtime', {
              type: 'lost',
              pos: r.realtime.pos,
              eval: lostEval
            })
            break
          }
          case 'BEST': {
            // 获取当前最佳点的评估值
            let currentEval = state.outputs.pv[0] ? state.outputs.pv[0].eval : null
            
            // 检查是否已经存在这个位置的点，如果存在则更新，否则添加
            let existingIndex = state.outputs.realtime.best.findIndex(p =>
              (p.pos[0] === r.realtime.pos[0] && p.pos[1] === r.realtime.pos[1])
            )
            
            if (existingIndex >= 0) {
              // 更新已存在的点
              commit('updateRealtimeEval', {
                type: 'best',
                index: existingIndex,
                evalValue: currentEval
              })
            } else {
              // 添加新的点
              commit('addRealtime', {
                type: 'best',
                pos: r.realtime.pos,
                eval: currentEval
              })
            }
            break
          }
          default:
            break
        }
      } else if (r.msg) {
        commit('setOutput', { key: 'msg', value: r.msg })
        commit('addMessage', r.msg)
      } else if (r.multipv) {
        if (r.multipv == 'DONE') commit('setCurrentPV', 0)
        else commit('setCurrentPV', +r.multipv)
      } else if (r.depth) {
        commit('setPVOutput', { key: 'depth', value: r.depth })
      } else if (r.seldepth) {
        commit('setPVOutput', { key: 'seldepth', value: r.seldepth })
      } else if (r.nodes) {
        commit('setPVOutput', { key: 'nodes', value: r.nodes })
      } else if (r.totalnodes) {
        commit('setOutput', { key: 'nodes', value: r.totalnodes })
      } else if (r.totaltime) {
        commit('setOutput', { key: 'time', value: r.totaltime })
      } else if (r.speed) {
        commit('setOutput', { key: 'speed', value: r.speed })
      } else if (r.eval) {
        commit('setPVOutput', { key: 'eval', value: r.eval })
      } else if (r.winrate) {
        commit('setPVOutput', { key: 'winrate', value: r.winrate })
      } else if (r.bestline) {
        commit('setPVOutput', { key: 'bestline', value: r.bestline })
      } else if (r.pos) {
        commit('setOutput', { key: 'pos', value: r.pos })
        commit('addUsedTime')
        commit('sortPV')
        commit('setThinkingState', false)
        
        // 分析模式下的特殊处理：合并realtime和PV数据，处理获胜局面
        if (rootState.settings.analyzeMode) {
          // 检查是否为获胜局面（bestline为空且eval为"-"）
          let isWinningPosition = false
          if (state.outputs.pv[0] && 
              (!state.outputs.pv[0].bestline || state.outputs.pv[0].bestline.length === 0) &&
              state.outputs.pv[0].eval === '-') {
            isWinningPosition = true
          }
          
          if (isWinningPosition) {
            // 获胜局面：显示获胜信息，并显示bestMove点
            if (state.posCallback) {
              // 创建获胜局面的realtime数据，显示bestMove点
              let winningRealtimeBest = []
              if (r.pos) {
                winningRealtimeBest.push({
                  pos: r.pos,
                  eval: '+M1'  // 获胜局面显示+M1
                })
              }
              
              state.posCallback({
                analysisResult: {
                  type: 'winning',
                  message: '当前局面已获胜',
                  bestMove: r.pos,
                  pv: state.outputs.pv,
                  realtime: {
                    ...state.outputs.realtime,
                    best: winningRealtimeBest
                  },
                  eval: '+M1'  // 获胜局面评估值为+M1
                }
              })
            }
          } else {
            // 正常分析局面：合并realtime和PV数据
            // 1. 收集PV中的所有有效点
            let pvPoints = []
            for (let pvIndex = 0; pvIndex < state.outputs.pv.length; pvIndex++) {
              let pv = state.outputs.pv[pvIndex]
              if (pv.bestline && pv.bestline.length > 0) {
                pvPoints.push({
                  pos: pv.bestline[0],
                  eval: pv.eval || null,
                  depth: pv.depth || 0,
                  source: 'pv',
                  pvIndex: pvIndex
                })
              }
            }
            
            // 2. 收集realtime中的现有点
            let realtimePoints = state.outputs.realtime.best.map((point, index) => ({
              ...point,
              source: 'realtime',
              realtimeIndex: index
            }))
            
            // 3. 合并数据并去重
            let allPoints = [...realtimePoints, ...pvPoints]
            let uniquePoints = []
            let seenPositions = new Set()
            
            for (let point of allPoints) {
              let posKey = `${point.pos[0]},${point.pos[1]}`
              if (!seenPositions.has(posKey)) {
                seenPositions.add(posKey)
                uniquePoints.push(point)
              } else {
                // 如果位置已存在，优先使用PV的数据（更准确）
                let existingIndex = uniquePoints.findIndex(p => 
                  p.pos[0] === point.pos[0] && p.pos[1] === point.pos[1]
                )
                if (existingIndex >= 0 && point.source === 'pv') {
                  uniquePoints[existingIndex] = point
                }
              }
            }
            
            // 4. 按照PV顺序排序（PV中的点优先）
            uniquePoints.sort((a, b) => {
              // 优先显示PV中的点
              if (a.source === 'pv' && b.source !== 'pv') return -1
              if (a.source !== 'pv' && b.source === 'pv') return 1
              // 都是PV点，按PV索引排序
              if (a.source === 'pv' && b.source === 'pv') {
                return a.pvIndex - b.pvIndex
              }
              // 都是realtime点，按realtime索引排序
              return a.realtimeIndex - b.realtimeIndex
            })
            
            // 5. 更新realtime数据
            let updatedRealtimeBest = uniquePoints.map(point => ({
              pos: point.pos,
              eval: point.eval
            }))
            
            // 使用新的mutation来完全替换realtime.best
            commit('setRealtimeBest', updatedRealtimeBest)
            
            // 6. 返回合并后的分析结果
            if (state.posCallback) {
              state.posCallback({
                analysisResult: {
                  type: 'analysis',
                  bestMove: r.pos,
                  pv: state.outputs.pv,
                  realtime: {
                    ...state.outputs.realtime,
                    best: updatedRealtimeBest
                  },
                  eval: state.outputs.pv[0] ? state.outputs.pv[0].eval : null
                }
              })
            }
          }
        } else {
          // 正常模式下实际下棋
          if (state.posCallback) state.posCallback(r.pos)
        }
      } else if (r.swap) {
        commit('setOutput', { key: 'swap', value: r.swap })
      } else if (r.forbid) {
        commit('setOutput', { key: 'forbid', value: r.forbid })
      } else if (r.attackFour) {
        commit('setOutput', { key: 'attackFour', value: r.attackFour })
      } else if (r.vcf) {
        commit('setOutput', { key: 'vcf', value: r.vcf })
      } else if (r.livingThree) {
        commit('setOutput', { key: 'livingThree', value: r.livingThree })
      } else if (r.threatAnalysis) {
        commit('setOutput', { key: 'threatAnalysis', value: r.threatAnalysis })
      } else if (r.error) {
        commit('setOutput', { key: 'error', value: r.error })
        commit('addMessage', 'Error: ' + r.error)
      } else if (r.ok) {
        commit('addMessage', 'Engine ready.')
        commit('setReady', true)
        dispatch('checkForbid')
        // 不在初始化时立即检查冲四点，避免与禁手检查冲突
      } else if (r.loading) {
        commit('setLoadingProgress', r.loading.progress)
      }
    }
    const engineURL = await engine.init(callback)
    commit('addMessage', 'Engine: ' + engineURL)
  },
  sendInfo({ rootState, rootGetters }) {
    engine.sendCommand('INFO RULE ' + rootState.settings.rule)
    engine.sendCommand('INFO THREAD_NUM ' + (rootState.settings.threads || 1))
    engine.sendCommand('INFO CAUTION_FACTOR ' + rootState.settings.candRange)
    engine.sendCommand('INFO STRENGTH ' + rootState.settings.strength)
    engine.sendCommand('INFO TIMEOUT_TURN ' + rootGetters['settings/turnTime'])
    engine.sendCommand('INFO TIMEOUT_MATCH ' + rootGetters['settings/matchTime'])
    engine.sendCommand('INFO MAX_DEPTH ' + rootGetters['settings/depth'])
    engine.sendCommand('INFO MAX_NODE ' + rootGetters['settings/nodes'])
    engine.sendCommand('INFO SHOW_DETAIL ' + (rootState.settings.showDetail ? 3 : 2))
    engine.sendCommand('INFO PONDERING ' + (rootState.settings.pondering ? 1 : 0))
    engine.sendCommand('INFO SWAPABLE ' + (rootState.position.swaped ? 0 : 1))
  },
  sendBoard({ rootState }, immediateThink) {
    let position = rootState.position.position

    let command = immediateThink ? 'BOARD' : 'YXBOARD'
    let side = position.length % 2 == 0 ? 1 : 2
    for (let pos of position) {
      command += ' ' + pos[0] + ',' + pos[1] + ',' + side
      side = 3 - side
    }
    command += ' DONE'
    engine.sendCommand(command)
  },
  think({ commit, dispatch, state, rootState, rootGetters }, args) {
    if (!state.ready) {
      commit('addMessage', 'Engine is not ready!')
      return
    }
    
    // 如果已经在思考中，直接返回
    if (state.thinking) {
      commit('addMessage', 'Engine is already thinking!')
      return Promise.resolve(null)
    }
    
    // 每次开始思考时都清除之前的分析数据
    commit('clearRealtime', 'best')
    commit('clearRealtime', 'lost')
    
    commit('setThinkingState', true)
    commit('setOutput', { key: 'swap', value: false })
    commit('clearMessage')

    dispatch('reloadConfig')
    dispatch('updateHashSize')
    dispatch('sendInfo')

    // 只在必要时重新启动引擎（分析模式下避免频繁重启）
    if (state.restart || state.startSize != rootState.position.size) {
      engine.sendCommand('START ' + rootState.position.size)
      commit('setRestart', false)
      commit('setStartSize', rootState.position.size)
      commit('clearUsedTime')
    }

    let timeLeft = Math.max(rootGetters['settings/matchTime'] - state.timeUsed, 1)
    engine.sendCommand('INFO TIME_LEFT ' + timeLeft)

    dispatch('sendBoard', false)
    commit('setThinkStartTime')
    commit('setLastThinkPosition', rootState.position.position)
    commit('clearOutput')

    if (args && args.balanceMode) {
      engine.sendCommand(
        (args.balanceMode == 2 ? 'YXBALANCETWO ' : 'YXBALANCEONE ') + (args.balanceBias || 0)
      )
    } else {
      // 根据分析模式决定行为
      // if (state.analyzeMode) {
      //   // 分析模式：使用更稳定的参数，确保分析结果一致性
      //   engine.sendCommand('INFO MAX_DEPTH 20')                    // 固定深度20
      //   engine.sendCommand('INFO MAX_NODE 10000000')               // 固定节点数1000万
      //   engine.sendCommand('INFO TIMEOUT_TURN 30000')              // 30秒时间限制
      //   engine.sendCommand('YXNBEST 5')                            // 显示5个点
      //   engine.sendCommand('YXTHINK')  // 发送思考命令
      // } else {
      //   // 正常模式：正常下棋
      //   engine.sendCommand('YXNBEST ' + rootState.settings.nbest)
      //   engine.sendCommand('YXTHINK')  // 发送思考命令
      // }
      
      // 分析模式和普通模式保持一致
      engine.sendCommand('YXNBEST ' + rootState.settings.nbest)
      engine.sendCommand('YXTHINK')  // 发送思考命令
    }

    return new Promise((resolve) => {
      commit('setPosCallback', resolve)
    })
  },
  stop({ commit, state, rootState }) {
    if (!state.thinking) return

    if (engine.stopThinking()) {
      commit('setReady', false)
      // commit('clearRealtime', 'best')
      // commit('clearRealtime', 'lost')
      commit('addUsedTime')
      commit('sortPV')
      commit('setThinkingState', false)
      let pos = state.outputs.pv[0].bestline[0]
      if (pos) {
        commit('setOutput', { key: 'pos', value: pos })
        if (state.posCallback) {
          // 分析模式下返回分析结果
          if (rootState.settings.analyzeMode) {
            // 检查是否为获胜局面
            let isWinningPosition = false
            if (state.outputs.pv[0] && 
                (!state.outputs.pv[0].bestline || state.outputs.pv[0].bestline.length === 0) &&
                state.outputs.pv[0].eval === '-') {
              isWinningPosition = true
            }
            
            if (isWinningPosition) {
              // 创建获胜局面的realtime数据，显示bestMove点
              let winningRealtimeBest = []
              if (pos) {
                winningRealtimeBest.push({
                  pos: pos,
                  eval: '+M1'  // 获胜局面显示+M1
                })
              }
              
              state.posCallback({
                analysisResult: {
                  type: 'winning',
                  message: '当前局面已获胜',
                  bestMove: pos,
                  pv: state.outputs.pv,
                  realtime: {
                    ...state.outputs.realtime,
                    best: winningRealtimeBest
                  },
                  eval: '+M1'  // 获胜局面评估值为+M1
                }
              })
            } else {
              state.posCallback({
                analysisResult: {
                  type: 'analysis',
                  bestMove: pos,
                  pv: state.outputs.pv,
                  realtime: state.outputs.realtime,
                  eval: state.outputs.pv[0] ? state.outputs.pv[0].eval : null
                }
              })
            }
          } else {
            state.posCallback(pos)
          }
        }
      }
      commit('setRestart')
      // Reset to initial state
      commit('setCurrentConfig', null)
      commit('setHashSize', null)
      return true
    }

    return false
  },
  restart({ commit }) {
    commit('setRestart')
    commit('clearUsedTime')
    commit('clearOutput')
  },
  reloadConfig({ commit, state, rootState }) {
    if (rootState.settings.configIndex == state.currentConfig) return
    commit('setCurrentConfig', rootState.settings.configIndex)

    engine.sendCommand('RELOADCONFIG ' + (CONFIGS[state.currentConfig] || ''))
  },
  updateHashSize({ commit, state, rootState }) {
    if (rootState.settings.hashSize == state.hashSize) return
    commit('setHashSize', rootState.settings.hashSize)

    engine.sendCommand('INFO HASH_SIZE ' + state.hashSize * 1024)
    commit('addMessage', `Hash size reset to ${state.hashSize} MB.`)
  },
  checkForbid({ commit, state, dispatch, rootState, rootGetters }) {
    commit('setOutput', { key: 'forbid', value: [] })
    if (!state.ready) return

    if (rootGetters['settings/gameRule'] == RENJU) {
      dispatch('sendInfo')
      if (state.restart || state.startSize != rootState.position.size) {
        engine.sendCommand('START ' + rootState.position.size)
        commit('setRestart', false)
        commit('setStartSize', rootState.position.size)
      }
      dispatch('sendBoard', false)
      engine.sendCommand('YXSHOWFORBID')
    }
  },
  
  checkAttackFour({ commit, state, dispatch, rootState }) {
    commit('setOutput', { key: 'attackFour', value: [] })
    
    // 增加更多安全检查
    if (!state.ready || state.thinking) return
    if (!rootState.settings.showAttackFour) return  // 如果冲四点开关关闭，直接返回
    
    try {
      dispatch('sendInfo')
      if (state.restart || state.startSize != rootState.position.size) {
        engine.sendCommand('START ' + rootState.position.size)
        commit('setRestart', false)
        commit('setStartSize', rootState.position.size)
      }
      dispatch('sendBoard', false)
      engine.sendCommand('YXSHOWATTACKFOUR')  // 使用新的冲四点命令
    } catch (error) {
      console.error('AttackFour check error:', error)
      commit('setOutput', { key: 'attackFour', value: [] })
    }
  },

  checkLivingThree({ commit, state, dispatch, rootState }) {
    commit('setOutput', { key: 'livingThree', value: [] })
    
    // 增加安全检查
    if (!state.ready || state.thinking) return
    if (!rootState.settings.showLivingThree) return  // 如果活三点开关关闭，直接返回
    
    try {
      dispatch('sendInfo')
      if (state.restart || state.startSize != rootState.position.size) {
        engine.sendCommand('START ' + rootState.position.size)
        commit('setRestart', false)
        commit('setStartSize', rootState.position.size)
      }
      dispatch('sendBoard', false)
      engine.sendCommand('YXSHOWLIVINGTHREE')
    } catch (error) {
      console.error('LivingThree check error:', error)
      commit('setOutput', { key: 'livingThree', value: [] })
    }
  },

  checkThreatAnalysis({ commit, state, dispatch, rootState }) {
    // 不再预先清空威胁分析数据，保持上一次的结果直到新结果返回
    // commit('setOutput', { key: 'threatAnalysis', value: {
    //   black: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 },
    //   white: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 }
    // }})
    
    // 增加安全检查
    if (!state.ready || state.thinking) return
    if (!rootState.settings.showThreatAnalysis) return  // 如果威胁分析开关关闭，直接返回
    
    try {
      dispatch('sendInfo')
      if (state.restart || state.startSize != rootState.position.size) {
        engine.sendCommand('START ' + rootState.position.size)
        commit('setRestart', false)
        commit('setStartSize', rootState.position.size)
      }
      dispatch('sendBoard', false)
      engine.sendCommand('YXSHOWTHREATANALYSIS')
    } catch (error) {
      console.error('ThreatAnalysis check error:', error)
      commit('setOutput', { key: 'threatAnalysis', value: {
        black: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 },
        white: { NONE: 0, FORBID: 0, L: 0, K: 0, J: 0, I: 0, H: 0, G: 0, F: 0, E: 0, D: 0, C: 0, B: 0, A: 0 }
      }})
    }
  },

  checkVCF({ commit, state, dispatch, rootState }) {
    commit('setOutput', { key: 'vcf', value: [] })
    
    // 增加安全检查
    if (!state.ready || state.thinking) return
    if (!rootState.settings.showVCF) return  // 如果VCF开关关闭，直接返回
    
    try {
      dispatch('sendInfo')
      if (state.restart || state.startSize != rootState.position.size) {
        engine.sendCommand('START ' + rootState.position.size)
        commit('setRestart', false)
        commit('setStartSize', rootState.position.size)
      }
      dispatch('sendBoard', false)
      engine.sendCommand('YXSHOWVCF')
    } catch (error) {
      console.error('VCF check error:', error)
      commit('setOutput', { key: 'vcf', value: [] })
    }
  },

  clearAllRealtime({ commit }) {
    commit('clearAllRealtime')
  },


}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
